/**
 * Single source of truth for the API base URL the frontend should call.
 *
 * Sub-apps under `apps/<name>/` are served on Firebase Hosting at their own host
 * `<name>.<env>.tricorder.icu` (plus the `<name>.mvp.*` aliases on the usb
 * backend). Firebase serves only the static bundle there — the `/api/**`
 * routing lives on the env's GCP LB at the PARENT host (`<env>.tricorder.icu`).
 * So the sub-app frontend must call the parent host explicitly, not its own
 * origin. Without that, a same-origin `/api/v1/me` request from the
 * sub-app hits Firebase, falls through to the SPA `index.html` (HTML, not
 * JSON), the auth-init parse fails, `isAuthenticated` stays false, and the user
 * gets bounced to login despite a valid session cookie scoped to the parent
 * (see PR #1534 + #1543 for the cookie sharing).
 *
 * The mapping is the platform CONVENTION — `apps/<name>` ⇒ `<name>.<env>` — so
 * this needs no app-name list and no label-count heuristic. Each app's build
 * injects its OWN subdomain via `VITE_APP_SUBDOMAIN` (= its `apps/<name>` dir,
 * see frontend/vite-app-preset.ts); we strip exactly that one prefix. The
 * main frontend (not under apps/) sets `VITE_APP_SUBDOMAIN=""` and falls through
 * to the relative `/api/v1`. The `.mvp` aliases work for free
 * (`alg.mvp.tricorder.icu` → strip `alg.` → `mvp.tricorder.icu/api/v1`).
 *
 * This helper was previously copy-pasted into four files (client.ts +
 * useAuthInit + useBackendHealth + useSessionEvents) with `care-planner.` hard-coded
 * into three of them — `yc.` added to client.ts only — which drifted: a fresh
 * load on a sub-app subdomain looked authenticated to the API client but not to
 * auth-init, and the redirect-to-login fired. Centralising here (and DERIVING
 * the prefix from the injected value instead of hard-coding app names) prevents
 * the next sub-app from reproducing it.
 */
const APP_SUBDOMAIN = import.meta.env.VITE_APP_SUBDOMAIN ?? "";

/**
 * Resolve the API base URL for the current execution context.
 *
 * Returns:
 *   - On this app's own cross-origin subdomain (hostname starts with
 *     `${VITE_APP_SUBDOMAIN}.`): the parent host's `/api/v1` (absolute URL),
 *     reusing the current protocol.
 *   - Otherwise: `import.meta.env.VITE_API_URL` if set, else the relative
 *     `/api/v1` (correct for the main app same-origin LB and for local dev).
 *
 * Server-side / non-browser callers fall through to the env-var path —
 * `typeof window === "undefined"` guards against SSR-time errors.
 */
export function getApiBaseUrl(): string {
  if (typeof window !== "undefined" && APP_SUBDOMAIN) {
    const prefix = `${APP_SUBDOMAIN}.`;
    if (window.location.hostname.startsWith(prefix)) {
      const parentDomain = window.location.hostname.slice(prefix.length);
      // Preserve the port: in prod hosts carry none (empty string → no
      // suffix), but on the local Host-routed nginx the sub-app runs at
      // compatibility.localhost:3000 — building from hostname alone sent the
      // API bootstrap to http://localhost/api (port 80, nothing listening)
      // and every sub-app load bounced to login.
      const portSuffix = window.location.port ? `:${window.location.port}` : "";
      return `${window.location.protocol}//${parentDomain}${portSuffix}/api/v1`;
    }
  }
  return import.meta.env.VITE_API_URL || "/api/v1";
}
